import { useState, useEffect, useRef } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { 
  MessageCircle, 
  Send, 
  Users,
  TrendingUp,
  TrendingDown,
  Clock
} from 'lucide-react'
import { useAgiAuthStore } from '../store/agiAuthStore'

interface ChatMessage {
  id: number
  message: string
  timestamp: string
  wallet_address: string
  bet_side: number | null
  bet_amount: number
  displayName: string
  betSideText: string | null
  betSideColor: string
  total_bet_amount: number
}

interface MarketChatProps {
  marketId: string
  creatorAddress?: string // Pass from parent
}

export default function MarketChat({ marketId, creatorAddress }: MarketChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [marketCreator, setMarketCreator] = useState<string | null>(creatorAddress || null)
  const { connected, publicKey } = useWallet()
  const { agiUser, isAgiAuthenticated } = useAgiAuthStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  // User can chat if wallet connected OR AGI authenticated
  const canChat = connected || isAgiAuthenticated

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Fetch chat messages and market creator
  const fetchMessages = async () => {
    try {
      const response = await axios.get(`/api/markets/${marketId}/chat`)
      setMessages(response.data.messages || [])
      
      // Also fetch market data to get creator if not provided
      if (!marketCreator) {
        const marketResponse = await axios.get(`/api/markets/${marketId}`)
        setMarketCreator(marketResponse.data.creator_address || null)
      }
    } catch (error) {
      console.error('Failed to fetch chat messages:', error)
    } finally {
      setLoading(false)
    }
  }

  // Send message
  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!canChat) {
      toast.error('Please connect your wallet or login with AGI to chat')
      return
    }

    if (!newMessage.trim()) {
      return
    }

    try {
      setSending(true)
      
      // Use wallet address if connected, otherwise use AGI user identifier
      const userIdentifier = publicKey 
        ? publicKey.toString() 
        : `agi:${agiUser?.id}:${agiUser?.email}`
      
      const displayName = publicKey
        ? undefined  // Server will generate from wallet
        : agiUser?.displayName || agiUser?.email?.split('@')[0]
      
      const response = await axios.post(`/api/markets/${marketId}/chat`, {
        message: newMessage.trim(),
        walletAddress: userIdentifier,
        displayName: displayName,
        isAgiUser: !publicKey && isAgiAuthenticated
      })

      if (response.data.success) {
        setNewMessage('')
        fetchMessages() // Refresh messages
      }
    } catch (error: any) {
      console.error('Failed to send message:', error)
      toast.error(error.response?.data?.error || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  // Auto-refresh messages every 10 seconds
  useEffect(() => {
    fetchMessages()
    const interval = setInterval(fetchMessages, 10000) // 10 seconds
    return () => clearInterval(interval)
  }, [marketId])

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Removed bet badges to make chat more anonymous and fun!

  return (
    <div className="card p-6 bg-[#001233] border-[#052393]/40">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-black text-white flex items-center">
          <MessageCircle className="w-6 h-6 mr-2 text-cpx-light-blue" />
          Pool Chat
        </h3>
        <div className="flex items-center space-x-1 text-sm text-gray-400">
          <Users className="w-4 h-4 text-cpx-light-blue" />
          <span className="font-bold">{messages.length} messages</span>
        </div>
      </div>

      {/* Compact Legend */}
      <div className="flex items-center space-x-3 mb-3 text-xs text-gray-400 bg-[#052393]/10 px-3 py-2 rounded-lg border border-[#052393]/30">
        <span className="text-gray-500">Badges:</span>
        <div className="flex items-center space-x-1.5">
          <span className="text-cpx-yellow">👑</span>
          <span>Creator</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <span className="text-cpx-yellow">⭐</span>
          <span>AGI</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <span className="text-cpx-green">✅</span>
          <span>YES</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <span className="text-red-400">❌</span>
          <span>NO</span>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="h-[600px] overflow-y-auto border border-[#052393]/40 rounded-lg p-4 mb-4 bg-[#001a3d]">
        {loading ? (
          <div className="text-center text-gray-400 py-8">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 text-cpx-light-blue animate-pulse" />
            <p className="font-bold">Loading chat...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-cpx-light-blue" />
            <p className="text-white font-bold mb-1">💬 No messages yet</p>
            <p className="text-sm">Be the first to start the conversation!</p>
            <p className="text-xs mt-2">🎯 Convince others to bet your way!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => {
              const isYesBet = msg.bet_side === 1
              const isNoBet = msg.bet_side === 0
              
              const isCreator = marketCreator && msg.wallet_address === marketCreator
              
              return (
                <div key={msg.id} className="group flex items-start space-x-2">
                  {/* Compact inline avatar */}
                  <div className="flex-shrink-0 relative group/avatar mt-0.5">
                    <div className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-black ${
                      isCreator ? 'bg-[#FBB03B]/30 border border-[#FBB03B]/50' :
                      isYesBet ? 'bg-[#009245]/20 border border-[#009245]/30' :
                      isNoBet ? 'bg-red-900/20 border border-red-500/30' :
                      'bg-[#052393]/30 border border-[#052393]/40'
                    }`} title={msg.displayName}>
                      {isCreator ? '👑' : msg.displayName.substring(0, 2).toUpperCase()}
                    </div>
                    {/* Hover tooltip */}
                    <div className="absolute left-0 top-8 bg-[#001233] border border-[#2AAAEA]/40 rounded px-2 py-1 text-[10px] font-mono opacity-0 group-hover/avatar:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap shadow-lg">
                      <div className="text-white">{msg.displayName}</div>
                      {isCreator && <div className="text-cpx-yellow font-bold">👑 Creator</div>}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm text-gray-200 rounded-lg px-3 py-2 border inline-block ${
                      isYesBet ? 'bg-[#009245]/5 border-[#009245]/20' :
                      isNoBet ? 'bg-red-900/5 border-red-500/20' :
                      'bg-[#052393]/10 border-[#052393]/30'
                    } group-hover:border-[#2AAAEA]/40 transition-colors`}>
                      {/* Inline bet badge */}
                      {(isYesBet || isNoBet) && (
                        <span className={`text-[10px] font-bold mr-1.5 ${
                          isYesBet ? 'text-cpx-green' : 'text-red-400'
                        }`}>
                          {isYesBet ? '✅' : '❌'}
                          {msg.bet_amount > 0 && ` ${msg.bet_amount.toLocaleString()}`}
                        </span>
                      )}
                      {msg.message}
                      {/* Timestamp inline at end */}
                      <span className="ml-2 text-[10px] text-gray-500">{formatTime(msg.timestamp)}</span>
                    </div>
                  </div>
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Send Message Form */}
      {canChat ? (
        <form onSubmit={sendMessage} className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={isAgiAuthenticated && !connected 
              ? `💬 Chatting as ${agiUser?.displayName || agiUser?.email?.split('@')[0]}...`
              : "💬 Share your thoughts... convince others to bet!"}
            className="flex-1 input text-sm"
            maxLength={500}
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white font-bold px-4 py-2 rounded-lg flex items-center space-x-2 transition-all disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sending && <span className="text-xs">Sending...</span>}
          </button>
        </form>
      ) : (
        <div className="text-center py-4 bg-[#052393]/20 border border-[#052393]/40 rounded-lg">
          <p className="text-sm text-gray-300 font-bold">🔒 Connect your wallet or login with AGI to chat</p>
        </div>
      )}
      
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-gray-400">
          💬 Discuss strategy • 🎯 Convince others • 🔥 Trash talk allowed!
        </span>
        <span className="text-gray-500">
          Updates every 10s
        </span>
      </div>
    </div>
  )
}
